import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Brain, Heart, Wine, Shield, Clock, Award, Users, Phone, MessageCircle,
  ArrowRight, CheckCircle, Star, MapPin, Stethoscope, Pill
} from "lucide-react";

const CONDITIONS = [
  { name: "Depression", slug: "depression", icon: Heart, desc: "Evidence-based treatment for all forms of depression" },
  { name: "Anxiety Disorders", slug: "anxiety", icon: Shield, desc: "GAD, panic disorder, social anxiety treatment" },
  { name: "Alcohol Addiction", slug: "alcohol-addiction", icon: Wine, desc: "Complete de-addiction & rehabilitation programs" },
  { name: "Drug Addiction", slug: "drug-addiction", icon: Pill, desc: "Comprehensive substance abuse recovery" },
  { name: "Sexual Health", slug: "erectile-dysfunction", icon: Heart, desc: "ED, PE, and performance anxiety treatment" },
  { name: "ADHD", slug: "adhd", icon: Brain, desc: "Diagnosis & treatment for children and adults" },
  { name: "Dementia", slug: "dementia", icon: Brain, desc: "Alzheimer's and memory disorder care" },
  { name: "Bipolar Disorder", slug: "bipolar-disorder", icon: Shield, desc: "Expert mood disorder management" },
  { name: "Schizophrenia", slug: "schizophrenia", icon: Brain, desc: "Comprehensive psychosis treatment" },
  { name: "OCD", slug: "ocd", icon: Shield, desc: "Medication and therapy for OCD" },
  { name: "Insomnia", slug: "insomnia", icon: Clock, desc: "Sleep disorder assessment and treatment" },
  { name: "PTSD", slug: "ptsd", icon: Shield, desc: "Trauma-focused evidence-based therapy" },
];

const SERVICES = [
  {
    title: "Neuropsychiatric Disorders",
    desc: "Expert care for brain-related psychiatric conditions including dementia, delirium, and neurological-psychiatric interface disorders.",
    icon: Brain,
    items: ["Dementia & Alzheimer's", "Delirium", "Brain-related disorders", "Cognitive assessments"],
  },
  {
    title: "Drug De-addiction",
    desc: "Punjab Government approved 15-bed inpatient facility. Complete treatment from detox to rehabilitation and relapse prevention.",
    icon: Wine,
    items: ["Alcohol addiction", "Substance abuse", "Cannabis & drugs", "Relapse prevention"],
  },
  {
    title: "Sexual Health",
    desc: "Certified sexologist providing confidential care for all sexual health concerns with evidence-based treatment approaches.",
    icon: Heart,
    items: ["Erectile dysfunction", "Performance anxiety", "Premature ejaculation", "Relationship issues"],
  },
];

const TRUST_POINTS = [
  { icon: Clock, label: "28+ Years Experience" },
  { icon: Award, label: "FIPS, MAPA Certified" },
  { icon: Users, label: "APA & WPA Member" },
  { icon: Stethoscope, label: "Sector 79, SAS Nagar" },
];

export default function HomePage() {
  return (
    <div>
      {/* Hero */}
      <section data-testid="hero-section" className="relative py-16 sm:py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-primary/5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-medium">
                <Star className="w-4 h-4" /> Top Rated Psychiatrist in Tricity
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-tight">
                Dr. Ambrish Singal
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-lg">
                <strong className="text-foreground">PSYCHIATRIST, DRUG DE-ADDICTION SPECIALIST & SEXOLOGIST</strong>
              </p>
              <p className="text-base text-muted-foreground max-w-lg leading-relaxed">
                MBBS, MD Psychiatry with 28+ years of experience. Neuropsychiatrist & Certified Sexologist based in Sector 79, Sahibzada Ajit Singh Nagar. Providing compassionate, evidence-based psychiatric care.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <a href="tel:+919915096633" data-testid="hero-call-btn">
                  <Button size="lg" className="gap-2 rounded-full text-base px-6">
                    <Phone className="w-5 h-5" /> Call Now
                  </Button>
                </a>
                <a
                  href="https://wa.me/919815333660?text=Hi%20Dr.%20Ambrish,%20I%20would%20like%20to%20book%20an%20appointment."
                  target="_blank" rel="noopener noreferrer"
                  data-testid="hero-whatsapp-btn"
                >
                  <Button size="lg" variant="outline" className="gap-2 rounded-full text-base px-6">
                    <MessageCircle className="w-5 h-5" /> WhatsApp
                  </Button>
                </a>
                <Link to="/contact" data-testid="hero-book-btn">
                  <Button size="lg" variant="secondary" className="gap-2 rounded-full text-base px-6">
                    Book Appointment <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 pt-4">
                {TRUST_POINTS.map((tp) => (
                  <div key={tp.label} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <tp.icon className="w-4 h-4 text-primary" />
                    <span>{tp.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://customer-assets.emergentagent.com/job_neuro-psych-hub/artifacts/w9ksd4ys_Screenshot%202026-04-15%20at%208.47.49%E2%80%AFAM.png"
                alt="Dr. Ambrish Singal - Psychiatrist, Drug De-addiction Specialist & Sexologist"
                className="rounded-2xl shadow-2xl w-full max-w-md mx-auto object-cover object-top aspect-[4/5]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section data-testid="conditions-section" className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-4">
              Conditions We Treat
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              Comprehensive psychiatric care for a wide range of mental health conditions. Each treatment plan is personalized to your specific needs.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {CONDITIONS.map((c) => (
              <Link key={c.slug} to={`/conditions/${c.slug}`} data-testid={`condition-${c.slug}`}>
                <Card className="h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer border">
                  <CardContent className="p-5 sm:p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-2.5 rounded-lg bg-primary/10 shrink-0">
                        <c.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-base mb-1">{c.name}</h3>
                        <p className="text-sm text-muted-foreground">{c.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section data-testid="services-section" className="py-16 sm:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-4">
              Specialized Services
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              Three core areas of expertise backed by decades of clinical experience and continuous professional development.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {SERVICES.map((s) => (
              <Card key={s.title} className="border hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 sm:p-8 space-y-4">
                  <div className="p-3 rounded-xl bg-primary/10 w-fit">
                    <s.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                  <ul className="space-y-2">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/services" data-testid="view-all-services">
              <Button variant="outline" className="gap-2 rounded-full">
                View All Services <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section data-testid="about-preview" className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_neuro-psych-hub/artifacts/w9ksd4ys_Screenshot%202026-04-15%20at%208.47.49%E2%80%AFAM.png"
                alt="Dr. Ambrish Singal"
                className="rounded-2xl shadow-lg w-full max-w-sm mx-auto object-cover object-top aspect-[3/4]"
              />
            </div>
            <div className="space-y-6">
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
                Why Choose Dr. Ambrish Singal?
              </h2>
              <div className="space-y-4">
                {[
                  { title: "28+ Years of Experience", desc: "Extensive clinical experience spanning government hospitals, Fortis Mohali (11 years as Director & HOD), and private practice." },
                  { title: "Internationally Recognized", desc: "Member of American Psychiatric Association, World Psychiatric Association, and Fellow of Indian Psychiatric Society." },
                  { title: "Certified Sexologist", desc: "Board-certified by American College of Sexologists, providing confidential sexual health care." },
                  { title: "Drug De-addiction Specialist", desc: "Punjab Government approved de-addiction programmes with comprehensive rehabilitation support." },
                  { title: "Evidence-Based Treatment", desc: "Combines latest psychopharmacology with proven psychotherapy approaches for optimal outcomes." },
                ].map((item) => (
                  <div key={item.title} className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-heading font-medium text-base">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/about" data-testid="learn-more-about">
                <Button className="gap-2 rounded-full mt-2">
                  Learn More About Dr. Singal <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section data-testid="location-section" className="py-16 sm:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-4">
              Serving the Tricity Region
            </h2>
            <p className="text-base text-muted-foreground">
              Conveniently located to serve patients from Mohali, Chandigarh, and Panchkula.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { city: "Mohali", desc: "Opposite Housefed Society 2, 704 HT Towers, Sector 79, Sahibzada Ajit Singh Nagar, Punjab 140308." },
              { city: "Chandigarh", desc: "Easily accessible from all sectors of Chandigarh. Just a short drive to our Mohali clinic." },
              { city: "Panchkula", desc: "Serving Panchkula residents with comprehensive psychiatric care. Prior appointment recommended." },
            ].map((loc) => (
              <Link key={loc.city} to={`/location/${loc.city.toLowerCase()}`} data-testid={`location-${loc.city.toLowerCase()}`}>
                <Card className="h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                  <CardContent className="p-6 space-y-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      <h3 className="font-heading font-semibold text-lg">{loc.city}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">{loc.desc}</p>
                    <span className="text-sm text-primary font-medium inline-flex items-center gap-1">
                      Learn more <ArrowRight className="w-3 h-3" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-16 sm:py-24 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-4">
            Take the First Step Towards Better Mental Health
          </h2>
          <p className="text-base opacity-90 mb-8 max-w-2xl mx-auto">
            You don't have to face it alone. Schedule a confidential consultation with Dr. Ambrish Singal today and begin your journey to recovery.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+919915096633" data-testid="cta-call-btn">
              <Button size="lg" variant="secondary" className="gap-2 rounded-full text-base px-6">
                <Phone className="w-5 h-5" /> Call +91-99150 96633
              </Button>
            </a>
            <a
              href="https://wa.me/919815333660?text=Hi%20Dr.%20Ambrish,%20I%20would%20like%20to%20book%20an%20appointment."
              target="_blank" rel="noopener noreferrer"
              data-testid="cta-whatsapp-btn"
            >
              <Button size="lg" variant="outline" className="gap-2 rounded-full text-base px-6 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                <MessageCircle className="w-5 h-5" /> WhatsApp
              </Button>
            </a>
            <Link to="/contact" data-testid="cta-book-btn">
              <Button size="lg" variant="outline" className="gap-2 rounded-full text-base px-6 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                Book Online
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
